import React, { useEffect, useState } from 'react';
import { Result, Button, Spin } from 'antd';
import { Link, useLocation } from 'react-router-dom';

import { useTeamAccess } from './useTeamAdminAccess';

const COPY = {
    manager: {
        title: "Team settings aren't available to you",
        subTitle: "Only the team owner and team managers can change a team's settings. If you need access, ask a manager of this team.",
    },
    member: {
        title: "This team isn't available to you",
        subTitle: 'Only people on this team can see it. If you should be a member, ask a manager of the team to invite you.',
    },
};

/**
 * How long to wait for someone who has just joined before saying anything else.
 *
 * Joining writes the member document; a trigger then adds the uid to the team
 * doc's `members` map, and that map is what every team rule reads. Until it
 * lands the joiner genuinely has no access — correctly, for a second or so. The
 * access hook is subscribed to the team doc, so it flips by itself the moment
 * the map updates; this window only decides what to show meanwhile.
 *
 * Generous on purpose. Showing "you can't see this" to someone who just joined
 * is worse than showing a spinner to someone whose trigger is slow.
 */
const SETUP_GRACE_MS = 20000;

/**
 * Renders its children only for people entitled to see them — `require="member"`
 * for anyone on the team, `require="manager"` (the default) for the owner, team
 * admins and managers. Strike admins pass either.
 *
 * Children are never mounted while the check is in flight, which is the point:
 * the class-based screens render first and check afterwards, so their Firestore
 * reads fire for whatever clientId is in the URL before any redirect lands.
 *
 * This is the presentation half of the fix. The database half is firestore.rules;
 * neither is sufficient alone, since a UI check can be bypassed and a rules
 * failure would otherwise surface as a broken-looking screen.
 */
export default function TeamAdminOnly({ teamId, backTo, require = 'manager', children }) {
    const { loading, allowed } = useTeamAccess(teamId, { require });
    const location = useLocation();
    // Set by the join flow on its way here. Only the screen that just performed a
    // join can know that "no access yet" means "not finished" rather than "not
    // yours", so nothing else gets the benefit of the doubt.
    const justJoined = !!(location.state && location.state.justJoined);
    const [stillSettingUp, setStillSettingUp] = useState(justJoined);

    useEffect(() => {
        if (!justJoined) return undefined;
        const timer = setTimeout(() => setStillSettingUp(false), SETUP_GRACE_MS);
        return () => clearTimeout(timer);
    }, [justJoined]);

    const backLink = backTo
        ? <Link to={backTo}><Button type="primary">Back to the team</Button></Link>
        : <Link to="/"><Button type="primary">Back to home</Button></Link>;

    if (loading) {
        return (
            <div style={{ padding: '3rem', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!allowed && justJoined && stillSettingUp) {
        return (
            <Result
                icon={<Spin size="large" />}
                title="Setting up your team"
                subTitle="You're on the team — we're just getting everything ready. This takes a few seconds."
            />
        );
    }

    if (!allowed && justJoined) {
        // The join succeeded — it is the setup behind it that is slow, so this
        // says what is true and what to do, rather than questioning membership.
        return (
            <Result
                status="info"
                title="Your team is still being set up"
                subTitle="You've joined and it's saved. Refresh this page in a moment and your team will be here."
                extra={backLink}
            />
        );
    }

    if (!allowed) {
        const { title, subTitle } = COPY[require] || COPY.manager;
        return (
            <Result
                status="403"
                title={title}
                subTitle={subTitle}
                extra={backLink}
            />
        );
    }

    return children;
}
